import React from 'react';
import { Barcode, Quote } from 'lucide-react';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
const VIAL_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/5eb5382b-2122-4ebe-bf1e-a310f0b11b99.jpg';
const INVOICE_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/9805fddc-cc40-495a-a849-cf91cc9f7267.jpg';
const DOCTOR_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/440e982d-705f-4a0e-9dc1-3f193429e1fe.jpg';
const comparison = [{
  id: 'farmacia',
  image: VIAL_IMAGE,
  alt: 'Vial de farmacia de vidrio con tapa de aluminio sobre un mostrador de mármol negro',
  tag: 'Vial de farmacia de élite',
  price: '$85',
  note: 'Grado médico. Mismo laboratorio.',
  accent: 'border-gold-400/45',
  priceClass: 'text-gold-300'
}, {
  id: 'clinica',
  image: INVOICE_IMAGE,
  alt: 'Factura de una clínica de lujo sobre un escritorio de mármol negro con una pluma fuente',
  tag: 'Factura de clínica en Beverly Hills',
  price: '$750',
  note: 'Mismo producto. Más mármol.',
  accent: 'border-crimson/40',
  priceClass: 'text-[#e08a91]'
}];
export function ProofSection() {
  return <section className="relative w-full overflow-hidden border-y border-white/[0.06] bg-ink-950 py-14 sm:py-20 md:py-28" data-id="element-201" data-mp="vok8o9eh03xx">
      <div className="relative mx-auto max-w-6xl px-5 sm:px-6" data-id="element-202" data-mp="rpbdd03s7y09">
        <SectionHeading label="El Contraste Visual" icon={Barcode} title={<>
              El mismo código de barras,{' '}
              <span className="gold-text" data-id="element-204" data-mp="1jh94sf62h7a0">diferente nivel de inteligencia</span>
            </>} data-id="element-203" data-mp="1oeiyw84ei746" />

        <div className="mt-9 grid gap-4 sm:mt-14 sm:grid-cols-2 sm:gap-6" data-id="element-205" data-mp="1amunk71z7mo9">
          {comparison.map((item, index) => <Reveal key={item.id} delay={index * 0.08} data-id="element-206" data-mp="274r1mp46uvju" data-mpi={index}>
              <figure className={`ease-luxe group relative overflow-hidden rounded-2xl border bg-ink-900 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.9)] transition-transform duration-300 hover:-translate-y-1.5 ${item.accent}`} data-id="element-207" data-mp="276bvsdmlcch6">
                <div className="relative aspect-[4/3] overflow-hidden" data-id="element-208" data-mp="5e08s045huhi">
                  <img src={item.image} alt={item.alt} width={1200} height={900} loading="lazy" decoding="async" sizes="(min-width: 640px) 50vw, 100vw" className="ease-luxe h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" data-id="element-209" data-mp="3mwcqyvqj7cj8" />
                  <span className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/20 to-transparent" aria-hidden="true" data-id="element-210" data-mp="33yi9ruf91upr" />
                  <span className={`absolute bottom-4 right-5 font-mono text-3xl font-bold ${item.priceClass}`} data-id="element-211" data-mp="1263tezscsybg">
                    {item.price}
                  </span>
                </div>
                <figcaption className="border-t border-white/[0.06] px-4 py-3.5 sm:px-5 sm:py-4" data-id="element-212" data-mp="2rkd5mta4gujw">
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ash" data-id="element-213" data-mp="2mls48qj4w4tv">
                    {item.tag}
                  </p>
                  <p className="mt-1.5 text-sm text-[#d3cfc7]" data-id="element-214" data-mp="2wlj3f5in380a">{item.note}</p>
                </figcaption>
              </figure>
            </Reveal>)}
        </div>

        <Reveal delay={0.1} className="mt-10 sm:mt-16" data-id="element-215" data-mp="36bvtfd983tdt">
          <blockquote className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-ink-800 to-ink-950 p-5 sm:p-9" data-id="element-216" data-mp="36bccnzvb7b1d">
            <span className="pointer-events-none absolute -right-20 -top-20 hidden h-56 w-56 rounded-full bg-gold-400/[0.06] blur-3xl sm:block" aria-hidden="true" data-id="element-217" data-mp="3fbsfd748ae56" />
            <Quote className="h-6 w-6 text-gold-400/70 sm:h-7 sm:w-7" strokeWidth={1.6} aria-hidden="true" data-id="element-218" data-mp="1kz2jijuauuv5" />
            <p className="relative mt-4 text-[14.5px] leading-relaxed text-[#dcd8d0] sm:mt-5 sm:text-[17px]" data-id="element-219" data-mp="1mhsotc9c5o5v">
              "Como profesional, me avergüenza lo que las clínicas cobran. Este protocolo es la guía
              más honesta y brutal que he visto.{' '}
              <span className="font-semibold text-gold-300" data-id="element-220" data-mp="21c71yw67y3cm">Ahorré $4,000 USD</span> en mis primeros
              dos ciclos. Si no compras esto, simplemente no te gusta el dinero."
            </p>
            <footer className="mt-5 flex items-center gap-3.5 sm:mt-7 sm:gap-4" data-id="element-221" data-mp="18ojkvr45puuq">
              <img src={DOCTOR_IMAGE} alt="Retrato del Dr. Alejandro V." width={48} height={48} loading="lazy" decoding="async" className="h-12 w-12 rounded-full border border-gold-400/40 object-cover" data-id="element-222" data-mp="1dm399jukjo0e" />
              <div data-id="element-223" data-mp="pvphzvjt5t6v">
                <p className="text-sm font-semibold text-[#eae7e1]" data-id="element-224" data-mp="pv7c077cg0lr">Dr. Alejandro V.</p>
                <p className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-ash" data-id="element-225" data-mp="335ph5wwtgz9n">
                  Biohacker independiente
                </p>
              </div>
            </footer>
          </blockquote>
        </Reveal>
      </div>
    </section>;
}